import React, { useEffect, useMemo } from 'react';
import './TermsPage.scss';
import { useTranslation } from 'react-i18next';
import LandingHeader from '../LandingPage/LandingHeader';
import Footer from '../LandingPage/Footer';
import CookieConsent from '../LandingPage/CookieConsent';
import { trackPageView } from '../../analytics';
import { parseLegalContent } from '../../utils/legalContent';

const TERMS_CONTENT_EN = `## 2.1 Acceptance of Terms

These Terms and Conditions ("Terms") govern your access to and use of the Artefact mobile application (the "App"), operated by FEEL IT SERVICES SAS (and its subsidiaries). By downloading, installing, or using the App, you agree to be bound by these Terms. If you do not agree, do not use the App.

## 2.2 Description of the Service

Artefact is a mobile application that allows users to scan museum exhibits and artworks using their device's camera, receive AI-generated informational content about the scanned items, and follow personalized visit itineraries. Content and identification results are provided for informational and educational purposes only.

## 2.3 Eligibility

You must be at least 16 years old, or the applicable minimum age of digital consent in your country, to create an account and use the App. If you are under this age, you may only use the App under the supervision of a parent or legal guardian who agrees to these Terms on your behalf.

## 2.4 Account Registration

To access certain features of the App, you may need to create an account. You agree to provide accurate and complete information and to keep your account credentials confidential. You are responsible for all activity that occurs under your account.

## 2.5 Acceptable Use

When using the App, you agree not to:

- Use the App for any unlawful purpose or in violation of these Terms;
- Attempt to gain unauthorized access to the App, its systems, or related networks;
- Reverse-engineer, decompile, or disassemble the App, except where permitted by law;
- Use automated means (bots, scrapers) to access or interact with the App;
- Upload or transmit content that is unlawful, infringing, defamatory, obscene, or otherwise objectionable;
- Interfere with or disrupt the integrity or performance of the App.

## 2.6 Intellectual Property

The App, including its design, features, graphics, logos, and underlying software, is owned by FEEL IT SERVICES SAS (and its subsidiaries) or its licensors and is protected by intellectual property laws. Museum and artwork content, images, and descriptions may be owned by the respective museums or third-party rights holders and are used under license or with permission. You are granted a limited, non-exclusive, non-transferable, revocable license to use the App for personal, non-commercial purposes only.

## 2.7 AI-Generated Content Disclaimer

The App uses artificial intelligence to identify artworks and exhibits and to generate related informational content. While we strive for accuracy, AI-generated identification and information may occasionally be incomplete, outdated, or incorrect. This content is provided for general informational and educational purposes and should not be relied upon as an authoritative or expert source. We are not liable for any decisions made in reliance on AI-generated content.

## 2.8 No In-App Purchases

The App does not currently offer in-app purchases, paid subscriptions, or any payment functionality. All features currently available in the App are provided free of charge. Should this change in the future, these Terms will be updated accordingly and users will be notified.

## 2.9 Third-Party Services

The App relies on third-party services (including analytics providers and AI-based image recognition services) to operate certain features. These third parties operate under their own terms and privacy practices. Our use of such services is described in our Privacy Policy.

## 2.10 Disclaimer of Warranties

The App is provided on an "as is" and "as available" basis, without warranties of any kind, whether express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, non-infringement, or accuracy of content. We do not warrant that the App will be uninterrupted, error-free, or free of harmful components.

## 2.11 Limitation of Liability

To the maximum extent permitted by applicable law, FEEL IT SERVICES SAS (and its subsidiaries) shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, revenue, or profits, arising out of or in connection with your use of, or inability to use, the App. Nothing in these Terms limits liability for gross negligence, willful misconduct, or matters that cannot be limited under applicable law.

## 2.12 Termination

We may suspend or terminate your access to the App at any time, with or without notice, if we believe you have violated these Terms. You may stop using the App and delete your account at any time. Provisions that by their nature should survive termination (e.g., intellectual property, disclaimers, limitation of liability) shall survive.

## 2.13 Governing Law and Jurisdiction

These Terms are governed by and construed in accordance with the laws of France, without regard to conflict-of-law principles. Any dispute arising out of or relating to these Terms or the App shall be subject to the exclusive jurisdiction of the competent courts of Paris, France, except where mandatory consumer protection laws of your country of residence provide otherwise.

## 2.14 Changes to These Terms

We may modify these Terms from time to time. We will notify you of material changes through the App or by other reasonable means. Continued use of the App after changes take effect constitutes your acceptance of the revised Terms.

## 2.15 Contact Us

For questions about these Terms, please contact us:

**FEEL IT SERVICES SAS (and its subsidiaries)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Phone: +33 6 62 88 36 50
Email: connect@feel-it-services.com`;

const TERMS_CONTENT_RO = `## 2.1 Acceptarea Termenilor

Acești Termeni și Condiții ("Termenii") reglementează accesul dumneavoastră la și utilizarea aplicației mobile Artefact ("Aplicația"), operată de FEEL IT SERVICES SAS (și filialele sale). Prin descărcarea, instalarea sau utilizarea Aplicației, sunteți de acord să respectați acești Termeni. Dacă nu sunteți de acord, nu utilizați Aplicația.

## 2.2 Descrierea serviciului

Artefact este o aplicație mobilă care permite utilizatorilor să scaneze exponate și opere de artă din muzee folosind camera dispozitivului lor, să primească conținut informativ generat de IA despre elementele scanate și să urmeze itinerarii de vizită personalizate. Conținutul și rezultatele identificării sunt oferite exclusiv în scopuri informative și educaționale.

## 2.3 Eligibilitate

Trebuie să aveți cel puțin 16 ani, sau vârsta minimă aplicabilă pentru consimțământul digital din țara dumneavoastră, pentru a vă crea un cont și a utiliza Aplicația. Dacă aveți o vârstă mai mică, puteți utiliza Aplicația doar sub supravegherea unui părinte sau tutore legal care este de acord cu acești Termeni în numele dumneavoastră.

## 2.4 Înregistrarea contului

Pentru a accesa anumite funcții ale Aplicației, este posibil să fie necesar să vă creați un cont. Sunteți de acord să furnizați informații corecte și complete și să păstrați confidențiale credențialele contului dumneavoastră. Sunteți responsabil pentru toate activitățile desfășurate prin contul dumneavoastră.

## 2.5 Utilizare acceptabilă

Atunci când utilizați Aplicația, sunteți de acord să nu:

- Utilizați Aplicația în orice scop ilegal sau cu încălcarea acestor Termeni;
- Încercați să obțineți acces neautorizat la Aplicație, la sistemele sale sau la rețelele conexe;
- Efectuați inginerie inversă, decompilare sau dezasamblare a Aplicației, cu excepția cazurilor permise de lege;
- Utilizați mijloace automatizate (roboți, scrapere) pentru a accesa sau interacționa cu Aplicația;
- Încărcați sau transmiteți conținut ilegal, care încalcă drepturi, defăimător, obscen sau altfel inacceptabil;
- Perturbați sau afectați integritatea sau performanța Aplicației.

## 2.6 Proprietate intelectuală

Aplicația, inclusiv designul, funcțiile, elementele grafice, siglele și software-ul aferent, este deținută de FEEL IT SERVICES SAS (și filialele sale) sau de licențiatorii săi și este protejată de legislația privind proprietatea intelectuală. Conținutul, imaginile și descrierile muzeelor și operelor de artă pot fi deținute de muzeele respective sau de titularii terți de drepturi și sunt utilizate sub licență sau cu permisiune. Vi se acordă o licență limitată, neexclusivă, netransferabilă și revocabilă de a utiliza Aplicația exclusiv în scopuri personale, necomerciale.

## 2.7 Declinarea răspunderii privind conținutul generat de IA

Aplicația utilizează inteligența artificială pentru a identifica opere de artă și exponate și pentru a genera conținut informativ aferent. Deși ne străduim să asigurăm acuratețea, identificarea și informațiile generate de IA pot fi, ocazional, incomplete, învechite sau incorecte. Acest conținut este oferit în scopuri generale, informative și educaționale, și nu ar trebui considerat o sursă autorizată sau de specialitate. Nu suntem răspunzători pentru nicio decizie luată pe baza conținutului generat de IA.

## 2.8 Fără achiziții în aplicație

În prezent, Aplicația nu oferă achiziții în aplicație, abonamente plătite sau vreo funcționalitate de plată. Toate funcțiile disponibile în prezent în Aplicație sunt oferite gratuit. Dacă această situație se va schimba în viitor, acești Termeni vor fi actualizați corespunzător, iar utilizatorii vor fi notificați.

## 2.9 Servicii terțe

Aplicația se bazează pe servicii terțe (inclusiv furnizori de analiză și servicii de recunoaștere a imaginilor bazate pe IA) pentru a asigura anumite funcții. Aceste părți terțe funcționează conform propriilor termeni și practici privind confidențialitatea. Utilizarea de către noi a acestor servicii este descrisă în Politica noastră de Confidențialitate.

## 2.10 Declinarea garanțiilor

Aplicația este furnizată "ca atare" și "în măsura disponibilității", fără niciun fel de garanții, exprese sau implicite, incluzând, dar fără a se limita la, garanții de vandabilitate, adecvare pentru un anumit scop, neîncălcare a drepturilor sau acuratețe a conținutului. Nu garantăm că Aplicația va funcționa neîntrerupt, fără erori sau fără componente dăunătoare.

## 2.11 Limitarea răspunderii

În limita maximă permisă de legislația aplicabilă, FEEL IT SERVICES SAS (și filialele sale) nu va fi răspunzătoare pentru niciun fel de daune indirecte, incidentale, speciale, subsecvente sau punitive, sau pentru pierderi de date, venituri sau profituri, rezultate din sau în legătură cu utilizarea de către dumneavoastră a Aplicației sau imposibilitatea de a o utiliza. Nimic din acești Termeni nu limitează răspunderea pentru neglijență gravă, conduită intenționată sau aspecte care nu pot fi limitate conform legislației aplicabile.

## 2.12 Încetare

Putem suspenda sau înceta accesul dumneavoastră la Aplicație în orice moment, cu sau fără notificare prealabilă, dacă considerăm că ați încălcat acești Termeni. Puteți înceta utilizarea Aplicației și puteți șterge contul în orice moment. Prevederile care, prin natura lor, trebuie să rămână în vigoare după încetare (de exemplu, proprietatea intelectuală, declinările de răspundere, limitarea răspunderii) vor rămâne aplicabile.

## 2.13 Legea aplicabilă și jurisdicția

Acești Termeni sunt guvernați și interpretați în conformitate cu legislația din Franța, fără a ține cont de principiile privind conflictul de legi. Orice litigiu care decurge din sau este legat de acești Termeni sau de Aplicație va fi supus jurisdicției exclusive a instanțelor competente din Paris, Franța, cu excepția cazului în care legislația imperativă privind protecția consumatorilor din țara dumneavoastră de reședință prevede altfel.

## 2.14 Modificări ale acestor Termeni

Putem modifica periodic acești Termeni. Vă vom notifica cu privire la modificările semnificative prin Aplicație sau prin alte mijloace rezonabile. Continuarea utilizării Aplicației după intrarea în vigoare a modificărilor constituie acceptarea de către dumneavoastră a Termenilor revizuiți.

## 2.15 Contactați-ne

Pentru întrebări despre acești Termeni, vă rugăm să ne contactați:

**FEEL IT SERVICES SAS (și filialele sale)**
13bis Avenue de la Motte Picquet, 75007 Paris, Franța
RCS Paris B 531 361 459
Telefon: +33 6 62 88 36 50
Email: connect@feel-it-services.com`;

const TERMS_CONTENT_FR = `## 2.1 Acceptation des Conditions

Les présentes Conditions générales (les « Conditions ») régissent votre accès à l'application mobile Artefact (l'« Application ») et son utilisation, exploitée par FEEL IT SERVICES SAS (et ses filiales). En téléchargeant, en installant ou en utilisant l'Application, vous acceptez d'être lié par les présentes Conditions. Si vous ne les acceptez pas, n'utilisez pas l'Application.

## 2.2 Description du service

Artefact est une application mobile qui permet aux utilisateurs de scanner des objets exposés et des œuvres d'art dans les musées à l'aide de l'appareil photo de leur appareil, de recevoir des contenus informatifs générés par IA sur les éléments scannés et de suivre des parcours de visite personnalisés. Les contenus et les résultats d'identification sont fournis à titre purement informatif et éducatif.

## 2.3 Conditions d'éligibilité

Vous devez être âgé d'au moins 16 ans, ou de l'âge minimum applicable de consentement numérique dans votre pays, pour créer un compte et utiliser l'Application. Si vous n'avez pas atteint cet âge, vous ne pouvez utiliser l'Application que sous la surveillance d'un parent ou d'un tuteur légal qui accepte les présentes Conditions en votre nom.

## 2.4 Création de compte

Pour accéder à certaines fonctionnalités de l'Application, vous pourriez devoir créer un compte. Vous vous engagez à fournir des informations exactes et complètes et à garder confidentiels vos identifiants de compte. Vous êtes responsable de toute activité effectuée depuis votre compte.

## 2.5 Utilisation acceptable

Lorsque vous utilisez l'Application, vous vous engagez à ne pas :

- Utiliser l'Application à des fins illicites ou en violation des présentes Conditions ;
- Tenter d'accéder sans autorisation à l'Application, à ses systèmes ou aux réseaux associés ;
- Procéder à de l'ingénierie inverse, décompiler ou désassembler l'Application, sauf dans les cas autorisés par la loi ;
- Utiliser des moyens automatisés (bots, scrapers) pour accéder à l'Application ou interagir avec elle ;
- Téléverser ou transmettre des contenus illicites, contrefaisants, diffamatoires, obscènes ou autrement répréhensibles ;
- Entraver ou perturber l'intégrité ou le fonctionnement de l'Application.

## 2.6 Propriété intellectuelle

L'Application, y compris sa conception, ses fonctionnalités, ses éléments graphiques, ses logos et ses logiciels sous-jacents, est la propriété de FEEL IT SERVICES SAS (et ses filiales) ou de ses concédants de licence et est protégée par les lois relatives à la propriété intellectuelle. Les contenus, images et descriptions relatifs aux musées et aux œuvres d'art peuvent appartenir aux musées concernés ou à des tiers titulaires de droits et sont utilisés sous licence ou avec autorisation. Il vous est concédé une licence limitée, non exclusive, non transférable et révocable d'utilisation de l'Application à des fins exclusivement personnelles et non commerciales.

## 2.7 Avertissement concernant les contenus générés par IA

L'Application utilise l'intelligence artificielle pour identifier les œuvres d'art et les objets exposés et pour générer des contenus informatifs associés. Bien que nous nous efforcions d'en assurer l'exactitude, l'identification et les informations générées par IA peuvent parfois être incomplètes, obsolètes ou inexactes. Ces contenus sont fournis à titre informatif et éducatif général et ne sauraient constituer une source faisant autorité ou une source d'expertise. Nous déclinons toute responsabilité quant aux décisions prises sur la base de contenus générés par IA.

## 2.8 Absence d'achats intégrés

L'Application ne propose actuellement aucun achat intégré, abonnement payant ni aucune fonctionnalité de paiement. Toutes les fonctionnalités actuellement disponibles dans l'Application sont fournies gratuitement. Si cela venait à changer, les présentes Conditions seraient mises à jour en conséquence et les utilisateurs en seraient informés.

## 2.9 Services tiers

L'Application s'appuie sur des services tiers (notamment des fournisseurs de services d'analyse et des services de reconnaissance d'images par IA) pour faire fonctionner certaines fonctionnalités. Ces tiers sont soumis à leurs propres conditions et pratiques en matière de confidentialité. Notre utilisation de ces services est décrite dans notre Politique de confidentialité.

## 2.10 Exclusion de garanties

L'Application est fournie « en l'état » et « selon disponibilité », sans garantie d'aucune sorte, expresse ou implicite, y compris, sans s'y limiter, les garanties de qualité marchande, d'adéquation à un usage particulier, d'absence de contrefaçon ou d'exactitude des contenus. Nous ne garantissons pas que l'Application fonctionnera sans interruption, sans erreur ou sans composants nuisibles.

## 2.11 Limitation de responsabilité

Dans toute la mesure permise par la loi applicable, FEEL IT SERVICES SAS (et ses filiales) ne saurait être tenue responsable de tout dommage indirect, accessoire, spécial, consécutif ou punitif, ni de toute perte de données, de chiffre d'affaires ou de bénéfices, résultant de ou lié à votre utilisation de l'Application ou à votre impossibilité de l'utiliser. Aucune disposition des présentes Conditions ne limite la responsabilité en cas de faute lourde, de faute intentionnelle ou pour les cas ne pouvant être limités en vertu de la loi applicable.

## 2.12 Résiliation

Nous pouvons suspendre ou résilier votre accès à l'Application à tout moment, avec ou sans préavis, si nous estimons que vous avez enfreint les présentes Conditions. Vous pouvez cesser d'utiliser l'Application et supprimer votre compte à tout moment. Les dispositions qui, par leur nature, doivent survivre à la résiliation (par exemple, la propriété intellectuelle, les exclusions de garanties, la limitation de responsabilité) demeureront en vigueur.

## 2.13 Droit applicable et juridiction compétente

Les présentes Conditions sont régies et interprétées conformément au droit français, sans égard aux principes de conflit de lois. Tout litige découlant des présentes Conditions ou de l'Application, ou s'y rapportant, relèvera de la compétence exclusive des tribunaux compétents de Paris, France, sauf lorsque les dispositions impératives de protection des consommateurs de votre pays de résidence en disposent autrement.

## 2.14 Modifications des présentes Conditions

Nous pouvons modifier les présentes Conditions de temps à autre. Nous vous informerons de toute modification importante via l'Application ou par tout autre moyen raisonnable. La poursuite de l'utilisation de l'Application après l'entrée en vigueur des modifications vaut acceptation des Conditions révisées.

## 2.15 Nous contacter

Pour toute question concernant les présentes Conditions, veuillez nous contacter :

**FEEL IT SERVICES SAS (et ses filiales)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Téléphone : +33 6 62 88 36 50
E-mail : connect@feel-it-services.com`;

const TERMS_CONTENT_RU = `## 2.1 Принятие Условий

Настоящие Условия использования («Условия») регулируют ваш доступ к мобильному приложению Artefact («Приложение»), управляемому FEEL IT SERVICES SAS (и её дочерними компаниями), и его использование. Скачивая, устанавливая или используя Приложение, вы соглашаетесь соблюдать настоящие Условия. Если вы не согласны с ними, не используйте Приложение.

## 2.2 Описание сервиса

Artefact — это мобильное приложение, которое позволяет пользователям сканировать музейные экспонаты и произведения искусства с помощью камеры своего устройства, получать сгенерированную ИИ информацию об отсканированных объектах и следовать персональным маршрутам посещения. Контент и результаты идентификации предоставляются исключительно в информационных и образовательных целях.

## 2.3 Право на использование

Чтобы создать учётную запись и пользоваться Приложением, вам должно быть не менее 16 лет или не менее минимального возраста цифрового согласия, установленного в вашей стране. Если вы не достигли этого возраста, вы можете пользоваться Приложением только под присмотром родителя или законного опекуна, который принимает настоящие Условия от вашего имени.

## 2.4 Регистрация учётной записи

Для доступа к некоторым функциям Приложения может потребоваться создать учётную запись. Вы обязуетесь предоставлять точную и полную информацию и сохранять конфиденциальность своих учётных данных. Вы несёте ответственность за все действия, совершаемые с использованием вашей учётной записи.

## 2.5 Допустимое использование

Используя Приложение, вы обязуетесь не:

- Использовать Приложение в незаконных целях или в нарушение настоящих Условий;
- Пытаться получить несанкционированный доступ к Приложению, его системам или связанным сетям;
- Осуществлять обратную разработку, декомпиляцию или дизассемблирование Приложения, за исключением случаев, разрешённых законом;
- Использовать автоматизированные средства (боты, скраперы) для доступа к Приложению или взаимодействия с ним;
- Загружать или передавать контент, который является незаконным, нарушающим права, клеветническим, непристойным или иным образом неприемлемым;
- Нарушать целостность или работу Приложения либо препятствовать им.

## 2.6 Интеллектуальная собственность

Приложение, включая его дизайн, функции, графику, логотипы и базовое программное обеспечение, принадлежит FEEL IT SERVICES SAS (и её дочерним компаниям) или её лицензиарам и охраняется законодательством об интеллектуальной собственности. Контент, изображения и описания музеев и произведений искусства могут принадлежать соответствующим музеям или сторонним правообладателям и используются по лицензии или с разрешения. Вам предоставляется ограниченная, неисключительная, непередаваемая, отзывная лицензия на использование Приложения исключительно в личных некоммерческих целях.

## 2.7 Оговорка о контенте, созданном ИИ

Приложение использует искусственный интеллект для идентификации произведений искусства и экспонатов и создания связанного с ними информационного контента. Хотя мы стремимся к точности, идентификация и информация, созданные ИИ, иногда могут быть неполными, устаревшими или неверными. Этот контент предоставляется в общих информационных и образовательных целях и не должен рассматриваться как авторитетный или экспертный источник. Мы не несём ответственности за решения, принятые на основе контента, созданного ИИ.

## 2.8 Отсутствие встроенных покупок

В настоящее время Приложение не предлагает встроенных покупок, платных подписок или каких-либо платёжных функций. Все функции, доступные в Приложении на данный момент, предоставляются бесплатно. Если в будущем это изменится, настоящие Условия будут соответствующим образом обновлены, а пользователи — уведомлены.

## 2.9 Сторонние сервисы

Для работы некоторых функций Приложение использует сторонние сервисы (в том числе поставщиков аналитики и сервисы распознавания изображений на основе ИИ). Эти третьи стороны действуют в соответствии со своими собственными условиями и политиками конфиденциальности. Использование нами таких сервисов описано в нашей Политике конфиденциальности.

## 2.10 Отказ от гарантий

Приложение предоставляется на условиях «как есть» и «по мере доступности», без каких-либо гарантий, явных или подразумеваемых, включая, помимо прочего, гарантии товарной пригодности, пригодности для определённой цели, ненарушения прав третьих лиц или точности контента. Мы не гарантируем, что Приложение будет работать бесперебойно, без ошибок или без вредоносных компонентов.

## 2.11 Ограничение ответственности

В максимальной степени, допускаемой применимым законодательством, FEEL IT SERVICES SAS (и её дочерние компании) не несёт ответственности за любые косвенные, случайные, особые, последующие или штрафные убытки, а также за любую потерю данных, доходов или прибыли, возникшие в результате или в связи с использованием вами Приложения либо невозможностью его использования. Ничто в настоящих Условиях не ограничивает ответственность за грубую небрежность, умышленные противоправные действия или в случаях, когда ответственность не может быть ограничена в соответствии с применимым законодательством.

## 2.12 Прекращение действия

Мы можем приостановить или прекратить ваш доступ к Приложению в любое время, с уведомлением или без него, если сочтём, что вы нарушили настоящие Условия. Вы можете прекратить использование Приложения и удалить свою учётную запись в любое время. Положения, которые по своей природе должны сохранять силу после прекращения действия (например, об интеллектуальной собственности, отказе от гарантий, ограничении ответственности), остаются в силе.

## 2.13 Применимое право и юрисдикция

Настоящие Условия регулируются и толкуются в соответствии с законодательством Франции без учёта коллизионных норм. Любой спор, возникающий из настоящих Условий или Приложения либо связанный с ними, подлежит исключительной юрисдикции компетентных судов Парижа, Франция, за исключением случаев, когда императивные нормы законодательства о защите прав потребителей страны вашего проживания предусматривают иное.

## 2.14 Изменения настоящих Условий

Мы можем время от времени изменять настоящие Условия. О существенных изменениях мы сообщим через Приложение или иными разумными способами. Продолжение использования Приложения после вступления изменений в силу означает ваше согласие с обновлёнными Условиями.

## 2.15 Свяжитесь с нами

По вопросам, касающимся настоящих Условий, пожалуйста, свяжитесь с нами:

**FEEL IT SERVICES SAS (и её дочерние компании)**
13bis Avenue de la Motte Picquet, 75007 Paris, France
RCS Paris B 531 361 459
Телефон: +33 6 62 88 36 50
Эл. почта: connect@feel-it-services.com`;

const TERMS_BLOCKS_EN = parseLegalContent(TERMS_CONTENT_EN);
const TERMS_BLOCKS_RO = parseLegalContent(TERMS_CONTENT_RO);
const TERMS_BLOCKS_FR = parseLegalContent(TERMS_CONTENT_FR);
const TERMS_BLOCKS_RU = parseLegalContent(TERMS_CONTENT_RU);

const TERMS_BLOCKS_BY_LANGUAGE = {
  ro: TERMS_BLOCKS_RO,
  fr: TERMS_BLOCKS_FR,
  ru: TERMS_BLOCKS_RU,
};

const TermsPage = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView('Terms & Conditions Page');
  }, []);

  const blocks = useMemo(
    () => TERMS_BLOCKS_BY_LANGUAGE[i18n.resolvedLanguage] || TERMS_BLOCKS_EN,
    [i18n.resolvedLanguage]
  );

  return (
    <div className="terms-page">
      <LandingHeader />

      <main className="terms-page__main">
        <span className="terms-page__eyebrow">
          {t('landing.termsPage.eyebrow', 'Legal')}
        </span>
        <h1 className="terms-page__title font-display">
          {t('landing.termsPage.title', 'Terms and Conditions')}
        </h1>
        <p className="terms-page__updated">
          {t('landing.termsPage.lastUpdated', 'Last updated: August 21, 2026')}
        </p>

        <div className="terms-page__content">
          {blocks.map((block, index) => {
            if (block.type === 'heading') {
              return (
                <h2 key={index} className="terms-page__heading">
                  {block.text}
                </h2>
              );
            }

            if (block.type === 'subheading') {
              return (
                <h3 key={index} className="terms-page__subheading">
                  {block.text}
                </h3>
              );
            }

            if (block.type === 'list') {
              return (
                <ul key={index} className="terms-page__list">
                  {block.items.map((item, itemIndex) => (
                    <li key={itemIndex}>
                      {item.label ? `${item.label}: ${item.text}` : item.text}
                    </li>
                  ))}
                </ul>
              );
            }

            return (
              <p key={index} className="terms-page__paragraph">
                {block.bold ? <strong>{block.text}</strong> : block.text}
              </p>
            );
          })}
        </div>
      </main>

      <Footer />
      <CookieConsent />
    </div>
  );
};

export default TermsPage;
